/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
/* eslint-disable max-classes-per-file */

import React, { useEffect, useState, useRef } from 'react';
import {
  Scene,
  ArcRotateCamera,
  Vector3,
  SSAO2RenderingPipeline,
  DefaultRenderingPipeline,
  DirectionalLight,
  ShadowGenerator,
  CubeTexture,
  Database,
  MeshBuilder,
  PBRMaterial,
  StandardMaterial,
  Color3,
  VertexData,
  HighlightLayer,
  Mesh,
} from '@babylonjs/core';

// eslint-disable-next-line import/no-extraneous-dependencies
import { ScreenshotTools } from '@babylonjs/core/Misc/screenshotTools';

import {
  AdvancedDynamicTexture,
} from '@babylonjs/gui';

import { BabylonObjectLoader, ObjectPart } from './BabylonObjectLoader';
import { BabylonComponentScene } from './BabylonScene';
import { SceneLightsEditUtil } from './sceneLightsEditUtil';
import { SceneObjectUtil } from './sceneObjectUtil';

import '@babylonjs/core/Debug/debugLayer'; // Augments the scene with the debug methods
import '@babylonjs/inspector';
import { ModelIlluminationData } from '../modelIllumination/modelIlluminationData';
import { SLTXFile } from '../fileFormats/sltx/sltxFile';
import { TreeOptionSelection, TreeOptionSelections } from '../modelConfiguration/data/optionsSelection';
import { FilePaths } from '../fileSystem/filePaths';
import { ModelGroupInfo } from '../fileFormats/sltx/modelGroupInfo';
import { SceneLightData } from '../modelIllumination/sceneLightData';
import { Vector } from '../types/vector/vector';

export type FncGetModelIlluminationData = () => ModelIlluminationData | undefined;
export type FncRegisterFncGetModelIlluminationData = (
  fnc : FncGetModelIlluminationData
) => void;

type BabylonComponentSceneManagerParameters = {

  // Model to load
  sltxFile : SLTXFile;

  // Model configuration to show
  composition: TreeOptionSelections;

  // Path to the skybox file
  skyboxPath : string;

  // Lights configuration
  modelIlluminationData : ModelIlluminationData;

  // Possible to edit Lights configuration
  modelIlluminationEditable : boolean;

  // If not null indicated that a screenshot of the scene should be taken
  // and sent to the POS back-end with the associated id
  screenshotId: string | undefined;

  cameraPosition: Vector3;
  setScreenshotHandler : (fileId : string, fileData: string) => void;

  // Function to register a function that can be called to retrieve the state of the lights
  // Basically, this component should call this function and provide a function to be called
  // when the parent wants to retrieve the state of the lights configuration
  registerCallbackGetModelIlluminationData :
  FncRegisterFncGetModelIlluminationData | undefined;
  setIsLoaded: (loaded: boolean) => void;
  setIsLoadingPart: (loaded: boolean) => void;
};

export function BabylonComponentSceneManager(
  {
    sltxFile,
    composition,
    skyboxPath,
    modelIlluminationData,
    modelIlluminationEditable,
    screenshotId,
    cameraPosition,
    setScreenshotHandler,
    registerCallbackGetModelIlluminationData,
    setIsLoaded,
    setIsLoadingPart,
  } : BabylonComponentSceneManagerParameters,
) : JSX.Element {

  const scene = useRef<Scene>();
  const activeSceneIdRef = useRef<number>(0);
  const screenshotTimerRef = useRef<number | undefined>();

  const cameraRef = useRef<ArcRotateCamera | null>(null);

  const [sceneObjects, setSceneObjects] = useState<Array<ObjectPart>>([]);
  const [selectedObjects, setselectedObjects] = useState<Array<ObjectPart>>([]);
  const [previousSelection, setPreviousSelection] = useState<TreeOptionSelections>();
  const [objectsToLoad, setObjectsToLoad] = useState<Array<{ groupId : number, optionId : number, loadObjectPromise:() => Promise<ObjectPart> }>>([]);
  // const [highlightLayer, setHighlightLayer] = useState<HighlightLayer>();
  const highlightLayerRef = useRef<HighlightLayer | null>(null);
  const highlightTimerRef = useRef<number | undefined>();
  const helperMeshesRef = useRef<Mesh[]>([]);
  const groundMeshRef = useRef<Mesh | null>(null);
  const ceilingMeshRef = useRef<Mesh | null>(null);
  const wallMeshRef = useRef<Mesh | null>(null);
  const sceneObjectsRef = useRef<ObjectPart[]>([]);
  const invalidateRenderRef = useRef<() => void>(() => {});

  const [actualScreenShotId, setActualScreenShotId] = useState<string | undefined>();
  const [secondaryScreenShotId, setSecondaryScreenShotId] = useState<string | undefined>();

  const sceneShadowGenerators = useRef<ShadowGenerator[]>([]);
  const lightsUiTextureRef = useRef<AdvancedDynamicTexture | null>(null);
  const lastAppliedIlluminationRef = useRef<ModelIlluminationData | undefined>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const useDebugFeatureHighlightMeshes = useRef<boolean>(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const useDebugFeatureOnlyOneMeshSelected = useRef<boolean>(true);

  const sceneRef = useRef(null);
  const [windowSize, setWindowSize] = useState({ width: 160, height: 90 });

  const isCurrentScene = (targetScene: Scene | undefined): targetScene is Scene => (
    targetScene !== undefined
    && scene.current === targetScene
    && targetScene.isDisposed === false
  );

  function isFulfilled<T>(
    result: PromiseSettledResult<T>,
  ): result is PromiseFulfilledResult<T> {
    return result.status === 'fulfilled';
  }

  function logRejectedLoaderResults<T>(
    context: string,
    results: PromiseSettledResult<T>[],
  ): void {
    results.forEach((result) => {
      if (result.status === 'rejected') {
        console.warn(`${context}: object load skipped/failed`, result.reason);
      }
    });
  }

  const invalidateNowAndNextFrame = () => {
    if (!isCurrentScene(scene.current)) {
      return;
    }

    invalidateRenderRef.current();

    requestAnimationFrame(() => {
      if (!isCurrentScene(scene.current)) {
        return;
      }

      invalidateRenderRef.current();
    });
  };

  useEffect(() => {

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const { width, height } = entries[0].contentRect;
        setWindowSize({ width, height });
      }
    });

    if (sceneRef.current) {
      resizeObserver.observe(sceneRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    sceneObjectsRef.current = sceneObjects;
  }, [sceneObjects]);

  const windowSizeRef = useRef(windowSize);

  useEffect(() => {
    windowSizeRef.current = windowSize;
  }, [windowSize]); // Update the ref whenever windowSize changes

  const sideCameraView = new Vector3(0, 0, 1);

  const initialCameraSettings = {
    alpha: 1,
    beta: Math.PI / 2,
    radius: 10,
    target: new Vector3(0, 0, 0),
  };

  const setupSceneCameras = (targetScene : Scene) => {
    const camera = new ArcRotateCamera(
      'Camera',
      initialCameraSettings.alpha,
      initialCameraSettings.beta,
      initialCameraSettings.radius,
      initialCameraSettings.target,
      targetScene,
      true,
    );

    camera.panningSensibility = 3000;
    camera.pinchPrecision = 1500;
    camera.speed = 0.5;

    const canvas = targetScene.getEngine().getRenderingCanvas();
    camera.attachControl(canvas, false);

    camera.onViewMatrixChangedObservable.add(() => {
      invalidateRenderRef.current();
    });

    cameraRef.current = camera;
  };

  const setupSceneVisual = (targetScene : Scene) => {

    const ssao = new SSAO2RenderingPipeline('ssao', targetScene, {
      ssaoRatio: 1, // Ratio of the SSAO post-process, in a lower resolution
      blurRatio: 1, // Ratio of the combine post-process (combines the SSAO and the scene)
    });

    ssao.radius = 8;
    ssao.totalStrength = 0.9;
    ssao.expensiveBlur = false;
    ssao.samples = 8;
    ssao.maxZ = 100;

    // Attach camera to the SSAO render pipeline
    targetScene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline(
      'ssao',
      targetScene.activeCamera,
    );

    const defaultPipeline = new DefaultRenderingPipeline(
      'default',
      true,
      targetScene,
      targetScene.activeCamera ? [targetScene.activeCamera] : [],
    );

    defaultPipeline.fxaaEnabled = true;
    defaultPipeline.samples = 4;
    // defaultPipeline.imageProcessing.vignetteEnabled = true;
    // defaultPipeline.imageProcessing.vignetteWeight = 4;
  };

  const setupLightSources = (
    targetScene : Scene,
    illuminationData : ModelIlluminationData,
  ) : ShadowGenerator[] | undefined => {

    // Iterate through lights setup array and create lights and shadow generators

    const shadowGenerators : ShadowGenerator[] = [];
    const shadowResolution = 1024;
    const shadowBlurriness = 32;

    const advancedTexture = modelIlluminationEditable
      ? AdvancedDynamicTexture.CreateFullscreenUI('UI')
      : undefined;

    lightsUiTextureRef.current = advancedTexture ?? null;

    // Setup environment lightning
    targetScene.environmentIntensity = illuminationData.environmentIntensity;

    // Setup individual lights
    illuminationData.lights.forEach((lightObject, lightIndex) => {

      // Create light
      const light = new DirectionalLight(
        lightObject.name,
        new Vector3(
          lightObject.direction.x,
          lightObject.direction.y,
          lightObject.direction.z,
        ),
        targetScene,
      );

      light.position = new Vector3(
        lightObject.position.x,
        lightObject.position.y,
        lightObject.position.z,
      );
      light.intensity = lightObject.intensity;

      // Create shadow generator
      if (lightObject.castShadows) {

        const shadowGenerator = new ShadowGenerator(shadowResolution, light);
        shadowGenerator.setDarkness(lightObject.shadowDarkness);
        shadowGenerator.useBlurExponentialShadowMap = true;
        shadowGenerator.useKernelBlur = true;
        shadowGenerator.blurKernel = shadowBlurriness;
        shadowGenerator.forceBackFacesOnly = true;
        shadowGenerators.push(shadowGenerator);
      }

      if (modelIlluminationEditable && advancedTexture) {
        SceneLightsEditUtil.CreateLightEditControl(
          light,
          lightIndex + 1,
          targetScene,
          advancedTexture,
        );
      }
    });

    if (modelIlluminationEditable && advancedTexture) {
      SceneLightsEditUtil.CreateEnvironmentLightControl(
        targetScene,
        advancedTexture,
      );
    }

    sceneShadowGenerators.current = [...shadowGenerators];
    return shadowGenerators;
  };

  const clearLightSources = (targetScene : Scene) : void => {
    sceneShadowGenerators.current.forEach((shadowGenerator) => {
      shadowGenerator.dispose();
    });
    sceneShadowGenerators.current = [];

    targetScene.lights.slice().forEach((light) => {
      light.dispose();
    });

    lightsUiTextureRef.current?.dispose();
    lightsUiTextureRef.current = null;
  };

  const rebuildLightSources = (
    targetScene : Scene,
    illuminationData : ModelIlluminationData,
  ) : void => {
    if (!isCurrentScene(targetScene)) {
      return;
    }

    clearLightSources(targetScene);

    const shadowGenerators = setupLightSources(targetScene, illuminationData);

    sceneObjectsRef.current.forEach((sceneObject) => {
      sceneObject.meshes?.meshes.forEach((mesh) => {
        shadowGenerators?.forEach((shadowGenerator) => {
          shadowGenerator.addShadowCaster(mesh, true);
        });
      });
    });

    const visibleObjectParts = getVisibleObjectPartsForComposition(
      sceneObjectsRef.current,
      composition,
    );

    if (visibleObjectParts.length > 0) {
      setupLightsPosition(targetScene, visibleObjectParts);
    }

    lastAppliedIlluminationRef.current = illuminationData;
    invalidateNowAndNextFrame();
  };

  const clearHelperMeshes = () => {
    helperMeshesRef.current.forEach((mesh) => {
      mesh.material?.dispose();
      mesh.dispose(false, true);
    });

    helperMeshesRef.current = [];
    groundMeshRef.current = null;
    ceilingMeshRef.current = null;
    wallMeshRef.current = null;
  };

  useEffect(() => {
    return () => {
      activeSceneIdRef.current += 1;

      if (screenshotTimerRef.current !== undefined) {
        window.clearTimeout(screenshotTimerRef.current);
        screenshotTimerRef.current = undefined;
      }

      if (isCurrentScene(scene.current)) {
        clearLightSources(scene.current);
      }

      clearHelperMeshes();

      scene.current = undefined;
      cameraRef.current = null;
    };
  }, []);

  const setupCameraPositionAndMovement = (
    targetScene: Scene,
    sceneObjectsToConsider: Array<ObjectPart>,
    cameraPos: Vector3,
  ): void => {
    if (!targetScene.activeCamera || !windowSizeRef.current) {
      return;
    }

    let objBBMin: Vector3 | undefined;
    let objBBMax: Vector3 | undefined;

    sceneObjectsToConsider.forEach((sceneObject) => {
      if (!sceneObject.meshes) return;

      sceneObject.meshes.meshes.forEach((mesh) => {
        if (mesh.getTotalVertices() <= 0) return;

        const bbox = mesh.getBoundingInfo().boundingBox;
        const bbMin = bbox.minimumWorld;
        const bbMax = bbox.maximumWorld;

        if (!objBBMin) {
          objBBMin = bbMin.clone();
          objBBMax = bbMax.clone();
        } else {
          objBBMin.x = Math.min(objBBMin.x, bbMin.x);
          objBBMin.y = Math.min(objBBMin.y, bbMin.y);
          objBBMin.z = Math.min(objBBMin.z, bbMin.z);

          objBBMax!.x = Math.max(objBBMax!.x, bbMax.x);
          objBBMax!.y = Math.max(objBBMax!.y, bbMax.y);
          objBBMax!.z = Math.max(objBBMax!.z, bbMax.z);
        }
      });
    });

    if (!objBBMin || !objBBMax) {
      return;
    }

    const center = objBBMin.add(objBBMax).scale(0.5);
    const size = objBBMax.subtract(objBBMin);
    const arcCamera = targetScene.activeCamera as ArcRotateCamera;

    // Camera tuning
    const refValue = Math.max(size.x, size.y, size.z);
    arcCamera.minZ = Math.max(0.01, refValue * 0.01);
    arcCamera.maxZ = Math.max(1000, refValue * 100);
    arcCamera.checkCollisions = true;
    arcCamera.collisionRadius = new Vector3(refValue * 0.2, refValue * 0.2, refValue * 0.2);
    arcCamera.upperRadiusLimit = refValue * 10;
    arcCamera.wheelPrecision = (0.5 / Math.max(refValue, 0.001)) * 1000;

    arcCamera.lowerBetaLimit = 0.1;
    arcCamera.upperBetaLimit = Math.PI * 0.99;

    // Desired view direction: from target toward camera
    const viewDir = cameraPos.clone().normalize();
    if (viewDir.lengthSquared() < 1e-6) {
      viewDir.copyFromFloats(0, 0, 1);
    }

    // Build camera basis
    let worldUp = Vector3.Up();
    if (Math.abs(Vector3.Dot(viewDir, worldUp)) > 0.999) {
      worldUp = new Vector3(0, 0, 1);
    }

    const right = Vector3.Cross(worldUp, viewDir).normalize();
    const up = Vector3.Cross(viewDir, right).normalize();

    // Bounding box corners
    const corners = [
      new Vector3(objBBMin.x, objBBMin.y, objBBMin.z),
      new Vector3(objBBMin.x, objBBMin.y, objBBMax.z),
      new Vector3(objBBMin.x, objBBMax.y, objBBMin.z),
      new Vector3(objBBMin.x, objBBMax.y, objBBMax.z),
      new Vector3(objBBMax.x, objBBMin.y, objBBMin.z),
      new Vector3(objBBMax.x, objBBMin.y, objBBMax.z),
      new Vector3(objBBMax.x, objBBMax.y, objBBMin.z),
      new Vector3(objBBMax.x, objBBMax.y, objBBMax.z),
    ];

    const aspect = windowSizeRef.current.width / Math.max(windowSizeRef.current.height, 1);

    // Babylon camera fov is vertical by default
    const vFov = arcCamera.fov;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);

    const tanH = Math.tan(hFov / 2);
    const tanV = Math.tan(vFov / 2);

    // Compute minimal distance so all 8 corners fit
    let requiredDistance = 0;

    corners.forEach((corner) => {
      const local = corner.subtract(center);

      const x = Math.abs(Vector3.Dot(local, right));
      const y = Math.abs(Vector3.Dot(local, up));
      const z = Vector3.Dot(local, viewDir);

      const dForX = x / Math.max(tanH, 1e-6) + z;
      const dForY = y / Math.max(tanV, 1e-6) + z;

      requiredDistance = Math.max(requiredDistance, dForX, dForY);
    });

    // Small padding so it fits nicely but does not look too far away
    const padding = 1.12;
    const radius = requiredDistance * padding;

    arcCamera.setTarget(center);
    arcCamera.radius = radius;
    arcCamera.setPosition(center.add(viewDir.scale(radius)));
  };

  const applyMeshGeometry = (
    targetScene: Scene,
    targetMesh: Mesh | null,
    sourceMesh: Mesh,
  ): Mesh => {
    if (!targetMesh) {
      return sourceMesh;
    }

    const vertexData = VertexData.ExtractFromMesh(sourceMesh);
    vertexData.applyToMesh(targetMesh, true);

    sourceMesh.material?.dispose();
    sourceMesh.dispose(false, true);

    if (!targetMesh.getScene()) {
      targetScene.addMesh(targetMesh);
    }

    return targetMesh;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setupWallGeometry = (
    targetScene : Scene,
    objectParts : Array<ObjectPart>,
    existingWall: Mesh | null = null,
  ) : Mesh => {
    const { vecObjCenter, vecObjSize } = SceneObjectUtil.getObjectCenterAndSize(objectParts);

    // Get largest side between width x length,
    // be sure the size is smaller than the maxZ of the camera (100x)
    const largestSide = Math.max(vecObjSize.x, vecObjSize.z, 0.001);
    const sideMultiplier = 39;
    const groundSize = sideMultiplier * largestSide;

    const sourceWall = MeshBuilder.CreateCylinder(
      existingWall ? '__tmpWallGeometry' : 'wall',
      {
        height: 200 * groundSize,
        diameter: groundSize,
        tessellation: 256,
      },
      targetScene,
    );

    const wall = applyMeshGeometry(targetScene, existingWall, sourceWall);

    wall.position = vecObjCenter.clone();
    wall.position.y -= vecObjSize.y * 2;

    if (!wall.material) {
      // non reflective but white...
      const wbpr = new StandardMaterial('material', targetScene);
      wbpr.diffuseColor = new Color3(0.0, 0.0, 0.0);
      wbpr.specularColor = new Color3(0, 0, 0);
      wbpr.emissiveColor = new Color3(1, 1, 1);

      // we'll always looking from inside..
      wbpr.backFaceCulling = false;
      wall.material = wbpr;
    }

    // we want the wall to kind of invisible...
    wall.receiveShadows = false;
    wall.setEnabled(true);
    return wall;
  };

  const setupGroundGeometry = (
    targetScene : Scene,
    objectParts : Array<ObjectPart>,
    existingGround: Mesh | null = null,
  ) : Mesh => {
    const { vecObjCenter, vecObjSize } = SceneObjectUtil.getObjectCenterAndSize(objectParts);

    // Get largest side between width and length
    const largestSide = Math.max(vecObjSize.x, vecObjSize.z, 0.001);
    const sideMultiplier = 270;
    const groundSide = sideMultiplier * largestSide;

    const sourceGround = MeshBuilder.CreateGround(
      existingGround ? '__tmpGroundGeometry' : 'myGround',
      {
        width: groundSide,
        height: groundSide,
        subdivisions: 32,
      },
      targetScene,
    );

    const ground = applyMeshGeometry(targetScene, existingGround, sourceGround);

    ground.position = vecObjCenter.clone();
    ground.position.y -= vecObjSize.y / 2.0;

    if (!ground.material) {
      const pbr = new PBRMaterial('pbr', targetScene);
      pbr.albedoColor = new Color3(1.0, 1.0, 1.0);
      pbr.metallic = 0.0;
      pbr.roughness = 1.0;
      ground.material = pbr;
    }

    ground.receiveShadows = true;
    ground.setEnabled(true);
    return ground;
  };

  const setupCeilingGeometry = (
    targetScene : Scene,
    objectParts : Array<ObjectPart>,
    existingCeiling: Mesh | null = null,
  ) : Mesh => {
    const { vecObjCenter, vecObjSize } = SceneObjectUtil.getObjectCenterAndSize(objectParts);

    // Use similar calculations as the ground for consistency in appearance
    const largestSide = Math.max(vecObjSize.x, vecObjSize.z, 0.001);
    const sideMultiplier = 270;
    const ceilingSide = sideMultiplier * largestSide;

    const sourceCeiling = MeshBuilder.CreateGround(
      existingCeiling ? '__tmpCeilingGeometry' : 'myCeiling',
      {
        width: ceilingSide,
        height: ceilingSide,
        subdivisions: 32,
      },
      targetScene,
    );

    const ceiling = applyMeshGeometry(targetScene, existingCeiling, sourceCeiling);

    // Position the ceiling above the objects
    ceiling.position = vecObjCenter.clone();
    ceiling.position.y += vecObjSize.y * 200; // Adjust this value as needed to place it at the right height

    if (!ceiling.material) {
      // Setting up the material similar to the ground for a non-reflective, whitish look
      const pbrCeiling = new PBRMaterial('pbrCeiling', targetScene);
      pbrCeiling.albedoColor = new Color3(1, 1, 1);
      pbrCeiling.metallic = 0.0;
      pbrCeiling.roughness = 1.0;
      pbrCeiling.backFaceCulling = false;
      ceiling.material = pbrCeiling;
    }

    ceiling.receiveShadows = false;
    ceiling.setEnabled(true);
    return ceiling;
  };

  const setupLightsPosition = (
    targetScene : Scene,
    objectParts : Array<ObjectPart>,
  ) => {

    const { vecObjCenter, vecObjSize } = SceneObjectUtil.getObjectCenterAndSize(objectParts);

    targetScene.lights.forEach((light) => {

      // Cast to the correct type of light we're using
      const directionalLight : DirectionalLight = light as DirectionalLight;

      // Start from the center of the object, distance light by the inverse of its direction
      // multiplied by the bounding box size (this will place the light at the edge of the
      // bounding box, following the inverse of its direction from the center)
      // Add an absolute distance factor (multiplied by the same inverse light direction) to
      // avoid shadow clipping issues (absolute instead of a percentage of the size of the
      // bounding box so that it's consistent, even with very large differences in geometry
      // size, from model to model)

      const distanceFactor = 0.5;
      const invNormDir = directionalLight.direction.normalize().negate();
      const distanceFactorVector = invNormDir.multiplyByFloats(
        distanceFactor,
        distanceFactor,
        distanceFactor,
      );
      directionalLight.position = vecObjCenter
        .add(invNormDir.multiply(vecObjSize))
        .add(distanceFactorVector);
    });
  };

  const isObjectPartVisibleInComposition = (
    sceneObject: ObjectPart,
    targetComposition: TreeOptionSelections,
  ): boolean => {
    if (sceneObject.groupID === -1 && sceneObject.optionID === -1) {
      return true;
    }

    const relevantCompositionPart = targetComposition.find(
      (compositionPart) => compositionPart.groupID === sceneObject.groupID,
    );

    return relevantCompositionPart !== undefined
      && relevantCompositionPart.optionID === sceneObject.optionID;
  };

  const getVisibleObjectPartsForComposition = (
    objectParts: ObjectPart[],
    targetComposition: TreeOptionSelections,
  ): ObjectPart[] => objectParts.filter(
    (sceneObject) => isObjectPartVisibleInComposition(sceneObject, targetComposition),
  );

  const refreshHelperGeometryForCurrentComposition = (
    targetScene: Scene,
    objectParts: ObjectPart[],
  ): void => {
    const visibleObjectParts = getVisibleObjectPartsForComposition(
      objectParts,
      composition,
    );

    if (visibleObjectParts.length === 0) {
      helperMeshesRef.current.forEach((mesh) => mesh.setEnabled(false));
      invalidateNowAndNextFrame();
      return;
    }

    const ground = setupGroundGeometry(
      targetScene,
      visibleObjectParts,
      groundMeshRef.current,
    );

    groundMeshRef.current = ground;
    ceilingMeshRef.current = null;
    wallMeshRef.current = null;
    helperMeshesRef.current = [ground];

    setupLightsPosition(targetScene, visibleObjectParts);
    invalidateNowAndNextFrame();
  };

  // Hide or show meshes based on the object composition
  // provided in the parameters
  const showObjectComposition = (
    sceneObjectsToConsider : ObjectPart[],
  ) : void => {
    if (sceneObjectsToConsider.length === 0) {
      return;
    }

    const targetGroups = new Map(
      composition.map((entry) => [entry.groupID, entry.optionID]),
    );

    sceneObjectsToConsider.forEach((sceneObject) => {
      if (sceneObject.meshes === undefined) {
        return;
      }

      const meshesVisible = isObjectPartVisibleInComposition(
        sceneObject,
        composition,
      );

      sceneObject.meshes.meshes.forEach((mesh) => {
        mesh.setEnabled(meshesVisible);
      });
    });

    invalidateNowAndNextFrame();
  };

  const disposeObjectParts = (objectParts: ObjectPart[]) => {
    objectParts.forEach((sceneObject) => {
      const importResult = sceneObject.meshes;
      if (!importResult) return;

      // Remove from shadow generators first
      importResult.meshes?.forEach((mesh) => {
        sceneShadowGenerators.current.forEach((shadowGenerator) => {
          shadowGenerator.removeShadowCaster(mesh, true);
        });
      });

      importResult.animationGroups?.forEach((animationGroup) => {
        animationGroup.dispose();
      });

      importResult.skeletons?.forEach((skeleton) => {
        skeleton.dispose();
      });

      importResult.transformNodes?.forEach((node) => {
        node.dispose(false, true);
      });

      importResult.meshes?.forEach((mesh) => {
        mesh.dispose(false, true);
      });

      sceneObject.meshes = undefined;
    });

  };

  const loadObjectToScene = (
    targetScene : Scene,
    shadowGenerators : ShadowGenerator[] | undefined,
  ) : void => {

    if (!isCurrentScene(targetScene)) {
      return;
    }

    // Cleanup objects
    disposeObjectParts(sceneObjectsRef.current);
    clearHelperMeshes();
    setSceneObjects([]);
    setObjectsToLoad([]);

    // Because the load of the object is asynchronous,
    // we save the Promises in this array so we can proceed when all is done
    const arrLoadObjectsOperationsImmediate : Array<() => Promise<ObjectPart>> = [];
    // const arrLoadObjectsOperationsDeferred : Array<() => Promise<ObjectPart>> = [];
    const arrLoadObjectsOperationsDeferred : Array<{ groupId : number, optionId : number, loadObjectPromise: () => Promise<ObjectPart> }> = [];

    if (sltxFile.isOpened() === false) {
      return;
    }

    // Load the parts of the object that are common among all variants
    let gltfFilePath : string = sltxFile.getCompletePath(
      sltxFile.getRootFileContents().objects,
    );

    let [dirPath, fileName] = FilePaths.splitFileFromPath(gltfFilePath);

    const localRemainDirPath = dirPath;
    const localRemainFileName = fileName;
    const localRemainGroupID = -1;
    const localRemainOptionID = -1;

    // Only do this if there is a file to load.
    // In some models there is no remain file, so we need to check

    if (localRemainFileName.length > 0) {
      arrLoadObjectsOperationsImmediate.push(
        () => BabylonObjectLoader(
          targetScene,
          localRemainDirPath,
          localRemainFileName,
          localRemainGroupID,
          localRemainOptionID,
          shadowGenerators,
        ),
      );
    }

    // Load all the components parts

    const modelGroups = sltxFile.getRootFileContents().groups;
    if (modelGroups) {
      modelGroups.forEach((group) => {
        const groupOptions = ModelGroupInfo.getGroupAllOptions(group);
        groupOptions.forEach((groupOption) => {
          gltfFilePath = sltxFile.getCompletePath(groupOption.objectRef);
          [dirPath, fileName] = FilePaths.splitFileFromPath(gltfFilePath);

          // Check if we need to load this option immediately

          const partNeededImmediately = composition.some((entry) => (
            entry.groupID === group.id
              && entry.optionID === groupOption.id
          ));

          const localDirPath = dirPath;
          const localFileName = fileName;
          const localGroupID = group.id;
          const localOptionID = groupOption.id;

          if (localFileName) { // Only ask to load if we've something to load...
            const fncLoadPart = () => BabylonObjectLoader(
              targetScene,
              localDirPath,
              localFileName,
              localGroupID,
              localOptionID,
              shadowGenerators,
            );

            // eslint-disable-next-line no-constant-condition
            if (partNeededImmediately) {
              arrLoadObjectsOperationsImmediate.push(fncLoadPart);
            } else {
              // arrLoadObjectsOperationsDeferred.push(fncLoadPart);
              arrLoadObjectsOperationsDeferred.push({ groupId: group.id, optionId: groupOption.id, loadObjectPromise: fncLoadPart });
            }
          }
        });
      });
    }

    setObjectsToLoad([...arrLoadObjectsOperationsDeferred]);

    setIsLoaded(false);

    Promise.allSettled(arrLoadObjectsOperationsImmediate.map((f) => f()))
      .then((arrResults) => {
        if (!isCurrentScene(targetScene)) {
          return;
        }

        logRejectedLoaderResults('Initial scene load', arrResults);

        const loadedSceneObjects = arrResults
          .filter(isFulfilled)
          .map((result) => result.value);

        if (loadedSceneObjects.length === 0) {
          setIsLoaded(false);
          return;
        }

        // Setup the camera, now that we have the object loaded.
        setupCameraPositionAndMovement(targetScene, loadedSceneObjects, cameraPosition);

        // Create extra scene helper geometry based on the current visible configuration.
        // This is important because the first loaded configuration is not necessarily
        // representative of later/deferred option objects.
        showObjectComposition(loadedSceneObjects);
        refreshHelperGeometryForCurrentComposition(targetScene, loadedSceneObjects);

        // Save the scene objects.
        setSceneObjects(loadedSceneObjects);

        // Redraw when ready, guarded because the scene may be disposed while async
        // model/shader work is still pending.
        invalidateNowAndNextFrame();
        targetScene.executeWhenReady(() => {
          if (!isCurrentScene(targetScene)) {
            return;
          }

          invalidateNowAndNextFrame();
        });

        const wereMeshesLoaded = loadedSceneObjects.every(
          (obj) => obj.meshes !== undefined && obj.meshes !== null,
        );

        if (wereMeshesLoaded) {
          setIsLoaded(true);
        }
      });
  };

  const setupSceneEnvironment = (targetScene : Scene) => {
    if (!isCurrentScene(targetScene)) {
      return;
    }

    targetScene.clearColor.set(1, 1, 1, 1);

    if (!skyboxPath) {
      targetScene.environmentTexture = null;
      return;
    }

    const reflectionTexture = CubeTexture.CreateFromPrefilteredData(
      skyboxPath,
      targetScene,
    );

    targetScene.environmentTexture = reflectionTexture;

    reflectionTexture.onLoadObservable.addOnce(() => {
      if (!isCurrentScene(targetScene)) {
        return;
      }

      invalidateNowAndNextFrame();
    });
  };

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current !== undefined) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = undefined;
      }

      highlightLayerRef.current?.dispose();
      highlightLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    let objectLoaders: Array<() => Promise<ObjectPart>> = [];
    let missingObjects: Array<{
      groupId: number,
      optionId: number,
      loadObjectPromise: () => Promise<ObjectPart>
    }> = [];

    const newSelection: TreeOptionSelection[] = [];

    if (previousSelection) {
      for (let i = 0; i < composition.length; i++) {
        const treeNodes = previousSelection.filter(
          (sn) => sn.groupID === composition[i].groupID
            && sn.optionID !== composition[i].optionID,
        );

        treeNodes.forEach((tn) => newSelection.push(tn));
      }
    }

    composition.forEach((c) => {
      const mObjects = objectsToLoad.filter(
        (object) => object.groupId === c.groupID && object.optionId === c.optionID,
      );

      objectLoaders = objectLoaders.concat(mObjects.map((obj) => obj.loadObjectPromise));
      missingObjects = missingObjects.concat(mObjects);
    });

    if (objectLoaders.length > 0) {
      setIsLoadingPart(true);

      const targetScene = scene.current;

      Promise.allSettled(objectLoaders.map((f) => f()))
        .then((arrResultsDeferred) => {
          if (!isCurrentScene(targetScene)) {
            return;
          }

          logRejectedLoaderResults('Deferred scene load', arrResultsDeferred);

          const loadedDeferredObjects = arrResultsDeferred
            .filter(isFulfilled)
            .map((result) => result.value);

          const remainObjectsToLoad = objectsToLoad.filter(
            (mo1) => !missingObjects.some(
              (mo2) => mo2.groupId === mo1.groupId && mo2.optionId === mo1.optionId,
            ),
          );

          setObjectsToLoad(remainObjectsToLoad);

          if (loadedDeferredObjects.length === 0) {
            setIsLoadingPart(false);
            return;
          }

          const allObjects = [...sceneObjectsRef.current, ...loadedDeferredObjects];

          setSceneObjects(allObjects);

          showObjectComposition(allObjects);
          refreshHelperGeometryForCurrentComposition(targetScene, allObjects);

          invalidateNowAndNextFrame();
          targetScene.executeWhenReady(() => {
            if (!isCurrentScene(targetScene)) {
              return;
            }

            invalidateNowAndNextFrame();
          });

          setIsLoadingPart(false);
        });

      return;
    }

    showObjectComposition(sceneObjects);
    setIsLoadingPart(false);

    if (isCurrentScene(scene.current)) {
      refreshHelperGeometryForCurrentComposition(scene.current, sceneObjects);
    }

    if (!isCurrentScene(scene.current)) {
      setPreviousSelection(composition);
      return;
    }

    if (!highlightLayerRef.current) {
      const hl = new HighlightLayer('HighlightLayer', scene.current);
      hl.blurHorizontalSize = 0.5;
      hl.blurVerticalSize = 0.5;
      hl.innerGlow = false;
      hl.outerGlow = true;

      highlightLayerRef.current = hl;

    }

    const hl = highlightLayerRef.current;

    if (highlightTimerRef.current !== undefined) {
      window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = undefined;
    }

    hl.removeAllMeshes();

    const objectsToHighlight: ObjectPart[] = [];

    if (newSelection.length > 0) {
      newSelection.forEach((c: TreeOptionSelection) => {
        const objs = sceneObjects.filter((obj) => c.groupID === obj.groupID);
        objectsToHighlight.push(...objs);
      });

      setselectedObjects(objectsToHighlight);
    } else if (selectedObjects && selectedObjects.length > 0) {
      objectsToHighlight.push(...selectedObjects);
    }

    objectsToHighlight.forEach((obj) => {
      obj.meshes?.meshes.forEach((abstractMesh) => {
        if (abstractMesh instanceof Mesh) {
          hl.addMesh(abstractMesh, Color3.Green());
        }
      });
    });
    if (objectsToHighlight.length > 0) {
      invalidateRenderRef.current();
    }

    if (objectsToHighlight.length > 0) {
      if (screenshotId === undefined) {
        highlightTimerRef.current = window.setTimeout(() => {
          if (!isCurrentScene(scene.current)) {
            highlightTimerRef.current = undefined;
            return;
          }

          hl.removeAllMeshes();
          highlightTimerRef.current = undefined;
          invalidateRenderRef.current();
        }, 1500);
      } else {
        hl.removeAllMeshes();
        invalidateRenderRef.current();
      }
    }

    setPreviousSelection(composition);
  }, [composition, sceneObjects, screenshotId]);

  const enableModelFilesCaching = (targetScene : Scene) => {
    const engine = targetScene.getEngine();
    Database.IDBStorageEnabled = true;
    engine.enableOfflineSupport = true;
    engine.disableManifestCheck = true;
  };

  const getModelIlluminationData = () : ModelIlluminationData | undefined => {

    if (!isCurrentScene(scene.current)) {
      return undefined;
    }

    const currentModelIlluminationData = new ModelIlluminationData();
    currentModelIlluminationData.environmentIntensity = scene.current.environmentIntensity;

    const sceneLights = scene.current.lights;
    sceneLights.forEach((sceneLight) => {

      const sceneDirectionalLight = sceneLight as DirectionalLight;

      const newSceneLight = new SceneLightData();

      newSceneLight.name = sceneLight.name;
      newSceneLight.direction = new Vector(
        sceneDirectionalLight.direction._x,
        sceneDirectionalLight.direction._y,
        sceneDirectionalLight.direction._z,
      );

      newSceneLight.position = new Vector(
        sceneDirectionalLight.position._x,
        sceneDirectionalLight.position._y,
        sceneDirectionalLight.position._z,
      );

      newSceneLight.intensity = sceneDirectionalLight.intensity;

      const sceneLightShadowGenerator = sceneShadowGenerators.current.find((shadowGen) => {
        const shadowLight = shadowGen.getLight();
        const shadowDirectionalLight = shadowLight as DirectionalLight;
        if (shadowDirectionalLight.name === sceneLight.name) {
          return true;
        }
        return false;
      });

      if (sceneLightShadowGenerator) {
        newSceneLight.castShadows = true;
        newSceneLight.shadowDarkness = sceneLightShadowGenerator.getDarkness();
      } else {
        newSceneLight.castShadows = false;
        newSceneLight.shadowDarkness = 0;
      }

      currentModelIlluminationData.lights.push(newSceneLight);
    });

    return currentModelIlluminationData;
  };

  useEffect(() => {
    if (!isCurrentScene(scene.current)) {
      return;
    }

    // The scene lights may have been changed locally by the gizmos.
    // Because of that, only rebuild when the parent explicitly sends a new
    // illumination object reference. Do not try to sync from the scene back
    // into this ref here; getModelIlluminationData() already reads the live
    // Babylon lights when the parent asks for them.
    if (lastAppliedIlluminationRef.current === modelIlluminationData) {
      return;
    }

    rebuildLightSources(scene.current, modelIlluminationData);
  }, [modelIlluminationData]);

  const takeScreenShot = async (dataUrl: string, filename : string) : Promise<void> => {
    setScreenshotHandler(filename, dataUrl);
  };

  useEffect(() => {
    if (!isCurrentScene(scene.current)) return;

    highlightLayerRef.current?.removeAllMeshes();

    // const camera = scene.current.getCameraByName('Camera');
    if (cameraRef.current) {
      setupCameraPositionAndMovement(scene.current, sceneObjects, sideCameraView);
      invalidateRenderRef.current();
    }

    if (actualScreenShotId && cameraRef.current != null) {
      invalidateRenderRef.current();
      const targetScene = scene.current;
      const targetCamera = cameraRef.current;

      screenshotTimerRef.current = window.setTimeout(() => {
        screenshotTimerRef.current = undefined;

        if (!isCurrentScene(targetScene) || !targetCamera) {
          return;
        }

        targetScene.render();

        ScreenshotTools.CreateScreenshotAsync(
          targetScene.getEngine(),
          targetCamera,
          { width: 800, height: 600 },
        ).then((data) => {
          if (!isCurrentScene(targetScene)) {
            return;
          }

          takeScreenShot(data, actualScreenShotId);
        });
      }, 2500);
    }
  }, [actualScreenShotId]);

  useEffect(() => {

    setActualScreenShotId(screenshotId);

  }, [secondaryScreenShotId]);

  useEffect(() => {

    highlightLayerRef.current?.removeAllMeshes();
    setSecondaryScreenShotId(screenshotId);

  }, [screenshotId]);

  useEffect(() => {
    if (!isCurrentScene(scene.current)) return;

    if (cameraRef.current) {
      setupCameraPositionAndMovement(scene.current, sceneObjects, cameraPosition);
      invalidateRenderRef.current();
    }
  }, [cameraPosition]);

  // Event handling
  const onSceneReady = (
    sceneReady: Scene,
    invalidateRender: () => void,
  ) => {
    activeSceneIdRef.current += 1;
    invalidateRenderRef.current = invalidateRender;

    scene.current = sceneReady;

    enableModelFilesCaching(sceneReady);

    setupSceneCameras(sceneReady);

    setupSceneVisual(sceneReady);

    const shadowGenerators = setupLightSources(sceneReady, modelIlluminationData);
    lastAppliedIlluminationRef.current = modelIlluminationData;

    loadObjectToScene(sceneReady, shadowGenerators);

    setupSceneEnvironment(sceneReady);

    invalidateRenderRef.current();

    setPreviousSelection(composition);

    if (registerCallbackGetModelIlluminationData) {
      registerCallbackGetModelIlluminationData(
        (): ModelIlluminationData | undefined => getModelIlluminationData(),
      );
    }
  };

  return (
    <div ref={sceneRef} style={{ width: '100%', height: '100%' }}>
      <BabylonComponentScene
        antiAlias={true}
        adaptToDeviceRatio={false}
        engineOptions={{}}
        sceneOptions={{}}
        callbackOnSceneReady={onSceneReady}
      />
    </div>
  );
}
